import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Modal, Spinner, Alert } from "react-bootstrap";
import { useUser } from "../../contextProviders/userContext";
import {
  Search,
  Calendar,
  Filter,
  X,
  Send,
  BellOff,
  Megaphone,
  FolderKanban,
  PlusCircle,
  Users,
} from "lucide-react";
import axios from "axios";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import NotificationIcon from "../../components/Notification";
import { API_BASE_URL } from "../../api";

// ── Helper Components (Consistent with Student side) ──

const Avatar = ({ name, surname, isProject }) => {
  const initials = `${name?.[0] || ""}${surname?.[0] || ""}`.toUpperCase() || "?";
  return (
    <div
      style={{
        width: "42px",
        height: "42px",
        borderRadius: "50%",
        background: isProject
          ? "linear-gradient(135deg, var(--color-teal), #3DBDAE)"
          : "linear-gradient(135deg, var(--color-primary), #2E52B2)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.85rem",
        flexShrink: 0,
        position: "relative",
      }}
    >
      {initials}
    </div>
  );
};

const TypeBadge = ({ isProject }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      padding: "0.22em 0.75em",
      borderRadius: "99px",
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      background: isProject ? "rgba(42,157,143,0.12)" : "rgba(27,58,135,0.10)",
      color: isProject ? "var(--color-teal)" : "var(--color-primary)",
      border: isProject
        ? "1px solid rgba(42,157,143,0.28)"
        : "1px solid rgba(27,58,135,0.20)",
      whiteSpace: "nowrap",
    }}
  >
    {isProject ? <FolderKanban size={10} /> : <Megaphone size={10} />}
    {isProject ? "Project" : "General Broadcast"}
  </span>
);

const StatCard = ({ label, value, accent }) => (
  <div
    style={{
      backgroundColor: "var(--color-surface)",
      border: "1px solid var(--color-border)",
      borderRadius: "var(--radius-md)",
      padding: "0.8rem 1.1rem",
      textAlign: "center",
      minWidth: "90px",
    }}
  >
    <div
      style={{
        fontSize: "1.4rem",
        fontWeight: 800,
        color: accent || "var(--color-primary)",
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: "3px", fontWeight: 600 }}>
      {label}
    </div>
  </div>
);

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      padding: "0.55rem 1rem",
      fontWeight: active ? 700 : 500,
      fontSize: "0.875rem",
      color: active ? "var(--color-primary)" : "var(--color-text-muted)",
      borderBottom: active ? "2px solid var(--color-primary)" : "2px solid transparent",
      cursor: "pointer",
      transition: "color var(--transition), border-color var(--transition)",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

// ── Main Component ──

const AdminAnnouncementsPage = () => {
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [activeTab, setActiveTab] = useState("all");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [stats, setStats] = useState({ total: 0, unread: 0, projects: 0 });

  // Creation form states
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedProjectForAnnouncement, setSelectedProjectForAnnouncement] = useState("global");

  const currentUserId = user?.id || user?._id || "";
  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  // Fetch all projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/projects`,
          getAuthHeader()
        );
        if (response.data && Array.isArray(response.data)) {
          setProjects(response.data);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (selectedProject) params.append("projectId", selectedProject);
      if (activeTab !== "all") params.append("filter", activeTab);
      if (searchQuery) params.append("search", searchQuery);
      if (dateRange.start) params.append("startDate", dateRange.start);
      if (dateRange.end) params.append("endDate", dateRange.end);

      const response = await axios.get(
        `${API_BASE_URL}/announcements?${params.toString()}`,
        getAuthHeader()
      );
      setAnnouncements(response.data);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setError("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const [unreadResponse, totalResponse, projectResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/announcements?filter=unread`, getAuthHeader()),
        axios.get(`${API_BASE_URL}/announcements`, getAuthHeader()),
        axios.get(`${API_BASE_URL}/announcements?filter=project`, getAuthHeader()),
      ]);

      setStats({
        unread: unreadResponse.data.length,
        total: totalResponse.data.length,
        projects: projectResponse.data.length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchStats();
  }, [activeTab, selectedProject, searchQuery, dateRange]);

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/announcements`,
        {
          title,
          message,
          projectId:
            !selectedProjectForAnnouncement || selectedProjectForAnnouncement === "global"
              ? null
              : selectedProjectForAnnouncement,
        },
        getAuthHeader()
      );
      setShowCreateModal(false);
      setTitle("");
      setMessage("");
      setSelectedProjectForAnnouncement("global");
      fetchAnnouncements();
      fetchStats();
    } catch (error) {
      console.error("Error creating announcement:", error);
      setError("Failed to create announcement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAnnouncement = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDetailModal(true);

    const isUnread = !announcement.recipients?.some(
      (r) => (r.user?._id || r.user || "").toString() === currentUserId.toString() && r.read
    );

    if (isUnread) {
      try {
        await axios.put(
          `${API_BASE_URL}/announcements/${announcement._id}/read`,
          {},
          getAuthHeader()
        );
        fetchAnnouncements();
        fetchStats();
      } catch (error) {
        console.error("Failed to mark announcement as read:", error);
      }
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const clearFilters = () => {
    setSearchQuery("");
    setDateRange({ start: "", end: "" });
    setSelectedProject("");
  };

  const hasFilters = searchQuery || dateRange.start || dateRange.end || selectedProject;

  const inputStyle = {
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "8px 12px",
    fontSize: "0.875rem",
    color: "var(--color-text)",
    background: "var(--color-surface)",
    outline: "none",
    width: "100%",
    transition: "border-color var(--transition), box-shadow var(--transition)",
  };

  const panelStyle = {
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-sm)",
  };

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{ backgroundColor: "var(--color-bg)", maxWidth: "100vw", overflowX: "hidden" }}
    >
      <Sidebar />

      <Container
        fluid
        className="py-3 px-4 flex-grow-1"
        style={{ maxHeight: "100vh", overflowY: "scroll" }}
      >
        <TopNavBar />

        {/* ── Page Header ── */}
        <Row className="align-items-center justify-content-between mt-2 mb-3 text-start">
          <Col xs={12} md={7}>
            <h4 style={{ fontWeight: 800, color: "var(--color-text)", letterSpacing: "-0.4px", marginBottom: "0.2rem" }}>
              Hi, {user?.name || "Admin"}
            </h4>
            <p style={{ fontSize: "0.875rem", color: "var(--color-text-muted)", marginBottom: 0 }}>
              Broadcast information and target specific program volunteers.
            </p>
          </Col>
          <Col xs={12} md={5} className="d-flex align-items-center justify-content-md-end gap-3 mt-3 mt-md-0">
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.45rem",
                backgroundColor: "var(--color-primary)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--radius-md)",
                padding: "0.5rem 1.1rem",
                fontWeight: 700,
                fontSize: "0.875rem",
                cursor: "pointer",
                transition: "background var(--transition), box-shadow var(--transition)",
                boxShadow: "var(--shadow-sm)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              <PlusCircle size={16} />
              Create Announcement
            </button>
            <NotificationIcon />
          </Col>
        </Row>

        {/* ── Stats Row ── */}
        <div className="d-flex gap-3 mb-4 flex-wrap">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Unread" value={stats.unread} accent="var(--color-red)" />
          <StatCard label="Project" value={stats.projects} accent="var(--color-teal)" />
        </div>

        {/* ── Filters ── */}
        <div style={{ ...panelStyle, padding: "1rem 1.25rem", marginBottom: "1rem" }}>
          <Row className="g-3 align-items-end text-start">
            <Col xs={12} sm={6} lg={3}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.35rem" }}>
                <Filter size={12} /> Target Project
              </label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                style={inputStyle}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.35rem" }}>
                <Search size={12} /> Keyword Search
              </label>
              <input
                type="text"
                placeholder="Search announcements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={inputStyle}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.35rem" }}>
                <Calendar size={12} /> Start Date
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                style={inputStyle}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.35rem" }}>
                <Calendar size={12} /> End Date
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                style={inputStyle}
              />
            </Col>
            {hasFilters && (
              <Col xs={12} className="d-flex justify-content-end">
                <button
                  onClick={clearFilters}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.4rem",
                    background: "transparent", border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)", color: "var(--color-text-secondary)",
                    fontSize: "0.8rem", fontWeight: 600, padding: "0.4rem 0.9rem", cursor: "pointer",
                    transition: "border-color var(--transition)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--color-primary)")}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
                >
                  <X size={13} /> Clear Filters
                </button>
              </Col>
            )}
          </Row>
        </div>

        {/* ── Announcement List Panel ── */}
        <div style={panelStyle}>
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: 0,
              borderBottom: "1px solid var(--color-border)",
              padding: "0 0.75rem",
              overflowX: "auto",
            }}
          >
            {[
              { key: "all", label: "All Announcements" },
              { key: "unread", label: `Unread${stats.unread > 0 ? ` (${stats.unread})` : ""}` },
              { key: "project", label: "Project Announcements" },
              { key: "sent", label: "Sent by Me" },
            ].map((tab) => (
              <TabBtn key={tab.key} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                {tab.label}
              </TabBtn>
            ))}
          </div>

          {/* List */}
          <div style={{ padding: "1rem 1.25rem" }} className="text-start">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "var(--color-primary)", width: "2rem", height: "2rem" }} />
                <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem", marginTop: "0.75rem" }}>
                  Loading announcements…
                </p>
              </div>
            ) : error ? (
              <div style={{
                padding: "1rem", borderRadius: "var(--radius-md)",
                background: "rgba(193,57,43,0.08)", color: "var(--color-red)",
                border: "1px solid rgba(193,57,43,0.2)", fontSize: "0.875rem",
              }}>
                {error}
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-5">
                <BellOff size={40} style={{ color: "var(--color-border)", marginBottom: "0.75rem" }} />
                <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", margin: 0 }}>
                  No announcements found.
                </p>
              </div>
            ) : (
              announcements.map((ann) => {
                const isProject = !!ann.projectId;
                const isUnread = !ann.recipients?.some(
                  (r) => (r.user?._id || r.user || "").toString() === currentUserId.toString() && r.read
                );

                return (
                  <div
                    key={ann._id}
                    onClick={() => handleOpenAnnouncement(ann)}
                    style={{
                      display: "flex",
                      gap: "0.9rem",
                      padding: "1rem 1.1rem",
                      marginBottom: "0.65rem",
                      backgroundColor: isUnread ? "rgba(27,58,135,0.03)" : "var(--color-bg)",
                      border: isUnread
                        ? "1.5px solid rgba(27,58,135,0.14)"
                        : "1.5px solid var(--color-border)",
                      borderRadius: "var(--radius-lg)",
                      cursor: "pointer",
                      position: "relative",
                      transition: "box-shadow var(--transition), transform var(--transition)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "var(--shadow-md)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {/* Unread stripe */}
                    {isUnread && (
                      <div style={{
                        position: "absolute", left: 0, top: "14px", bottom: "14px",
                        width: "3px", borderRadius: "0 3px 3px 0",
                        backgroundColor: "var(--color-primary)",
                      }} />
                    )}

                    <Avatar name={ann.sender?.name} surname={ann.sender?.surname} isProject={isProject} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title row */}
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.3rem" }}>
                        <span style={{ fontWeight: 700, fontSize: "0.97rem", color: "var(--color-text)", lineHeight: 1.3 }}>
                          {ann.title}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                          <TypeBadge isProject={isProject} />
                          {isUnread && (
                            <span style={{
                              background: "rgba(193,57,43,0.1)", color: "var(--color-red)",
                              border: "1px solid rgba(193,57,43,0.22)", borderRadius: "99px",
                              fontSize: "0.67rem", fontWeight: 700, padding: "0.15em 0.55em",
                            }}>
                              New
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Meta */}
                      <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)", marginBottom: "0.45rem", display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                        <span>By <strong style={{ color: "var(--color-primary)" }}>{ann.sender?.name} {ann.sender?.surname}</strong></span>
                        <span>·</span>
                        <span>{formatDate(ann.createdAt)}</span>
                        {isProject && (
                          <>
                            <span>·</span>
                            <span style={{ color: "var(--color-teal)", fontWeight: 600 }}>{ann.projectId?.name}</span>
                          </>
                        )}
                      </div>

                      {/* Snippet */}
                      <p style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", lineHeight: 1.55, margin: 0 }}>
                        {ann.message.length > 200 ? `${ann.message.slice(0, 200)}…` : ann.message}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Detail Modal ── */}
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
          <Modal.Header
            closeButton
            style={{ borderBottom: "1px solid var(--color-border)", padding: "1rem 1.4rem" }}
          >
            <Modal.Title style={{ fontWeight: 700, fontSize: "1rem", color: "var(--color-primary)" }}>
              Announcement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "1.4rem" }} className="text-start">
            {selectedAnnouncement && (() => {
              const isProject = !!selectedAnnouncement.projectId;
              return (
                <div>
                  {/* Header */}
                  <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", marginBottom: "1.25rem" }}>
                    <Avatar
                      name={selectedAnnouncement.sender?.name}
                      surname={selectedAnnouncement.sender?.surname}
                      isProject={isProject}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", marginBottom: "0.3rem" }}>
                        <h5 style={{ fontWeight: 800, color: "var(--color-text)", fontSize: "1.1rem", margin: 0 }}>
                          {selectedAnnouncement.title}
                        </h5>
                        <TypeBadge isProject={isProject} />
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                        <span>By <strong style={{ color: "var(--color-primary)" }}>{selectedAnnouncement.sender?.name} {selectedAnnouncement.sender?.surname}</strong></span>
                        <span style={{ margin: "0 0.4rem" }}>·</span>
                        <span>{formatDate(selectedAnnouncement.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  <hr style={{ borderColor: "var(--color-border)", margin: "0 0 1.2rem" }} />

                  {/* Body */}
                  <p style={{ fontSize: "0.95rem", color: "var(--color-text-secondary)", lineHeight: 1.65, whiteSpace: "pre-line", marginBottom: "1.25rem" }}>
                    {selectedAnnouncement.message}
                  </p>

                  {/* Audience metadata */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: "0.75rem",
                    padding: "0.85rem 1rem",
                    background: "var(--color-bg)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                  }}>
                    <Users size={18} style={{ color: "var(--color-primary)", flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Target Audience
                      </div>
                      <div style={{ fontWeight: 600, color: "var(--color-text)", fontSize: "0.9rem" }}>
                        {isProject
                          ? `Volunteers of: ${selectedAnnouncement.projectId?.name}`
                          : "All WCCO Volunteers (Global Broadcast)"}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid var(--color-border)", padding: "0.85rem 1.4rem" }}>
            <Button
              onClick={() => setShowDetailModal(false)}
              style={{
                background: "var(--color-bg)", border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)", padding: "0.5rem 1.2rem",
                color: "var(--color-text-secondary)", fontWeight: 700, fontSize: "0.85rem",
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ── Create Announcement Modal ── */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
          <Modal.Header
            closeButton
            style={{ borderBottom: "1px solid var(--color-border)", padding: "1rem 1.4rem" }}
          >
            <Modal.Title style={{ fontWeight: 700, fontSize: "1rem", color: "var(--color-primary)" }}>
              Create New Announcement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "1.4rem" }} className="text-start">
            <Form onSubmit={handleCreateAnnouncement}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>
                  Target Audience
                </Form.Label>
                <Form.Select
                  value={selectedProjectForAnnouncement}
                  onChange={(e) => setSelectedProjectForAnnouncement(e.target.value)}
                  required
                  style={inputStyle}
                >
                  <option value="global">All Users (Global Announcement)</option>
                  <optgroup label="Specific Project Volunteers">
                    {projects.map((project) => (
                      <option key={project._id} value={project._id}>
                        {project.name}
                      </option>
                    ))}
                  </optgroup>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>
                  Title
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter announcement title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  style={inputStyle}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>
                  Message
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Type the message you want to broadcast here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  style={{ ...inputStyle, resize: "none" }}
                />
              </Form.Group>

              <div className="d-flex justify-content-end gap-2 mt-4 pt-2">
                <Button
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    background: "var(--color-bg)", border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)", padding: "0.5rem 1.2rem",
                    color: "var(--color-text-secondary)", fontWeight: 700, fontSize: "0.85rem",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  style={{
                    backgroundColor: "var(--color-primary)", border: "none",
                    borderRadius: "var(--radius-md)", padding: "0.5rem 1.5rem",
                    color: "#fff", fontWeight: 700, fontSize: "0.85rem",
                    display: "flex", alignItems: "center", gap: "0.4rem"
                  }}
                >
                  <Send size={14} />
                  {submitting ? "Sending..." : "Send Announcement"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminAnnouncementsPage;
